import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  pointerWithin, rectIntersection, closestCenter, getFirstCollision, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, X, Calendar, Tag, Users, ChevronDown,
  AlertCircle, Minus, ChevronsUp, Settings, KanbanSquare,
} from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const COLUMNS = [
  { id: 'todo',        label: '예정',    color: 'border-gray-600',   dot: 'bg-gray-400' },
  { id: 'in_progress', label: '진행중',  color: 'border-blue-500',   dot: 'bg-blue-400' },
  { id: 'done',        label: '완료',    color: 'border-green-500',  dot: 'bg-green-400' },
];

const PRIORITY_MAP = {
  low:    { label: '낮음',  icon: Minus,      color: 'text-gray-400' },
  medium: { label: '보통',  icon: AlertCircle, color: 'text-yellow-400' },
  high:   { label: '높음',  icon: ChevronsUp, color: 'text-red-400' },
};

const LABEL_COLORS = [
  '#6366f1','#8b5cf6','#ec4899','#ef4444','#f97316',
  '#eab308','#22c55e','#14b8a6','#3b82f6','#64748b',
];

function Avatar({ name = '', url }) {
  const [err, setErr] = useState(false);
  const initials = name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  if (url && !err) {
    return (
      <img src={url} alt={name} referrerPolicy="no-referrer"
        className="w-6 h-6 rounded-full object-cover flex-shrink-0 ring-1 ring-gray-700"
        onError={() => setErr(true)} />
    );
  }
  return (
    <span className="w-6 h-6 rounded-full bg-brand/30 text-brand text-[10px] font-bold flex items-center justify-center flex-shrink-0">
      {initials}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const p = PRIORITY_MAP[priority] || PRIORITY_MAP.medium;
  const Icon = p.icon;
  return (
    <span className={`flex items-center gap-0.5 text-[10px] ${p.color}`}>
      <Icon size={10} />{p.label}
    </span>
  );
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function KanbanCard({ card, isDragging }) {
  const isOverdue = card.dueDate && card.status !== 'done' && dayjs(card.dueDate).isBefore(dayjs(), 'day');
  const descText = stripHtml(card.description);

  return (
    <div
      className={`bg-gray-800 border rounded-xl p-3.5 group transition-all
        ${isDragging ? 'opacity-40 cursor-grabbing' : 'border-gray-700 hover:border-gray-600 cursor-pointer'}`}
    >
      {/* 라벨 */}
      {card.label && (
        <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full mb-2"
          style={{ backgroundColor: card.label.color + '33', color: card.label.color }}>
          {card.label.name}
        </span>
      )}

      {/* 제목 */}
      <p className="text-[15px] font-semibold text-white leading-snug mb-2">{card.title}</p>

      {/* 설명 미리보기 */}
      {descText && (
        <p className="text-xs text-gray-500 mb-2.5 line-clamp-2">{descText}</p>
      )}

      {/* 하단 메타 */}
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="flex items-center gap-2 flex-wrap">
          <PriorityBadge priority={card.priority} />
          {card.dueDate && (
            <span className={`flex items-center gap-0.5 text-[10px] ${isOverdue ? 'text-red-400' : 'text-gray-500'}`}>
              <Calendar size={10} />
              {dayjs(card.dueDate).format('MM.DD')}
            </span>
          )}
        </div>
        {/* 담당자 아바타 */}
        <div className="flex -space-x-1">
          {(card.assigneeDetails || []).slice(0, 3).map(a => (
            <Avatar key={a.id} name={a.name} url={a.avatarUrl} />
          ))}
          {(card.assigneeDetails || []).length > 3 && (
            <span className="w-6 h-6 rounded-full bg-gray-700 text-gray-400 text-[10px] flex items-center justify-center">
              +{card.assigneeDetails.length - 3}
            </span>
          )}
        </div>
      </div>

    </div>
  );
}

function SortableKanbanCard({ card, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      onClick={isDragging ? undefined : onOpen}>
      <KanbanCard card={card} isDragging={isDragging} />
    </div>
  );
}

function Column({ col, cards, onAddCard, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });

  return (
    <div className="flex flex-col flex-1 min-w-[260px]">
      {/* 컬럼 헤더 */}
      <div className={`flex items-center justify-between mb-3 pb-2 border-b-2 ${col.color}`}>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${col.dot}`} />
          <h3 className="text-sm font-semibold text-white">{col.label}</h3>
          <span className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded-full">{cards.length}</span>
        </div>
        <button onClick={() => onAddCard(col.id)}
          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
          <Plus size={14} />
        </button>
      </div>

      {/* 카드 목록 — 드롭 가능 영역 */}
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-2 flex-1 min-h-[120px] rounded-xl transition-colors ${isOver ? 'bg-gray-800/40' : ''}`}
      >
        <SortableContext items={cards.map(c => c.id)} strategy={verticalListSortingStrategy}>
          {cards.map(card => (
            <SortableKanbanCard key={card.id} card={card} onOpen={onOpen ? () => onOpen(card) : undefined} />
          ))}
        </SortableContext>
        {cards.length === 0 && (
          <div className={`flex-1 border-2 border-dashed rounded-xl flex items-center justify-center ${isOver ? 'border-gray-600' : 'border-gray-800'}`}>
            <button onClick={() => onAddCard(col.id)}
              className="text-xs text-gray-600 hover:text-gray-400 flex items-center gap-1 py-6">
              <Plus size={12} /> 카드 추가
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CardModal({ card, labels, admins, onClose, onSave }) {
  const [form, setForm] = useState({
    title: card?.title || '',
    description: card?.description || '',
    status: card?.status || 'todo',
    priority: card?.priority || 'medium',
    labelId: card?.labelId || '',
    assignees: card?.assignees || [],
    dueDate: card?.dueDate || '',
  });
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setForm(p => ({
    ...p,
    assignees: p.assignees.includes(id) ? p.assignees.filter(a => a !== id) : [...p.assignees, id],
  }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave({ ...form, labelId: form.labelId || null, dueDate: form.dueDate || null });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">{card ? '카드 수정' : '새 카드 추가'}</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          {/* 제목 */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">제목 *</label>
            <input
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="카드 제목" value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))} autoFocus required
            />
          </div>

          {/* 설명 */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">설명</label>
            <textarea
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand resize-none"
              placeholder="상세 내용 입력..." rows={3} value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>

          {/* 상태 + 우선순위 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">상태</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">우선순위</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                <option value="low">낮음</option>
                <option value="medium">보통</option>
                <option value="high">높음</option>
              </select>
            </div>
          </div>

          {/* 라벨 + 마감일 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">라벨</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.labelId} onChange={e => setForm(p => ({ ...p, labelId: e.target.value }))}>
                <option value="">없음</option>
                {labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">마감일</label>
              <input type="date" className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
            </div>
          </div>

          {/* 담당자 */}
          {admins.length > 0 && (
            <div>
              <label className="text-xs text-gray-400 mb-2 block">담당자</label>
              <div className="flex flex-wrap gap-2">
                {admins.map(a => {
                  const selected = form.assignees.includes(a.id);
                  return (
                    <button key={a.id} type="button" onClick={() => toggle(a.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs border transition-all ${
                        selected ? 'bg-brand/20 border-brand text-brand' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}>
                      <Avatar name={a.name} url={a.avatarUrl} />
                      <span>{a.name}</span>
                      {(a.team || a.department) && <span className="text-[10px] opacity-60">{a.team || a.department}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800">
              취소
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80 disabled:opacity-50">
              {saving ? '저장 중...' : card ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LabelModal({ labels, onClose, onSave, onDelete }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(LABEL_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const handleAdd = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name, color });
    setName('');
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">라벨 관리</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800"><X size={16} /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          {/* 기존 라벨 목록 */}
          <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
            {labels.map(l => (
              <div key={l.id} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-gray-800">
                <span className="flex items-center gap-2 text-sm text-white">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }} />
                  {l.name}
                </span>
                <button onClick={() => onDelete(l.id)} className="text-gray-600 hover:text-red-400"><X size={13} /></button>
              </div>
            ))}
            {labels.length === 0 && <p className="text-xs text-gray-600 text-center py-2">라벨이 없습니다</p>}
          </div>
          {/* 추가 폼 */}
          <form onSubmit={handleAdd} className="flex flex-col gap-2 pt-2 border-t border-gray-800">
            <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="새 라벨명" value={name} onChange={e => setName(e.target.value)} />
            <div className="flex flex-wrap gap-1.5">
              {LABEL_COLORS.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
            <button type="submit" disabled={saving}
              className="px-3 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80 disabled:opacity-50">
              {saving ? '추가 중...' : '+ 라벨 추가'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const DEPT_LABEL = { product: '상품개발본부', material: '소재개발본부', equipment: '장비개발본부' };

export default function KanbanPage({ department }) {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [labels, setLabels] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type: 'add'|'edit', card?, defaultStatus? }
  const [labelModal, setLabelModal] = useState(false);
  const [activeCard, setActiveCard] = useState(null); // DragOverlay용
  const [filterLabel, setFilterLabel] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const dragOriginStatus = useRef(null); // 드래그 시작 시 원래 status 보존
  const lastOverId = useRef(null);

  // 커스텀 충돌감지: 포인터가 컬럼 영역 위에 있으면 컬럼 id 반환 (빈 컬럼도 감지)
  const collisionDetection = useCallback((args) => {
    // 1) 포인터가 직접 닿은 droppable 우선 확인
    const pointerHits = pointerWithin(args);
    const hits = pointerHits.length > 0 ? pointerHits : rectIntersection(args);
    const overId = getFirstCollision(hits, 'id');

    if (overId != null) {
      lastOverId.current = overId;
      // 컬럼 위에 있으면 해당 컬럼 반환 (빈 컬럼 드롭 가능하게)
      if (COLUMNS.some(c => c.id === overId)) return [{ id: overId }];
      return [{ id: overId }];
    }
    // 드롭 가능 영역을 벗어났을 때 마지막 위치 유지
    return lastOverId.current ? [{ id: lastOverId.current }] : [];
  }, []);

  const load = useCallback(async () => {
    try {
      const deptParam = department ? `?department=${department}` : '';
      const [cardsRes, labelsRes, adminsRes] = await Promise.all([
        api.get(`/kanban/cards${deptParam}`),
        api.get('/kanban/labels'),
        api.get('/admins/dropdown'),
      ]);
      setCards(cardsRes.data);
      setLabels(labelsRes.data);
      setAdmins(adminsRes.data || []);
    } catch (e) {
      console.error('KanbanPage load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cardsInColumn = (status) => {
    return cards.filter(c => {
      if (c.status !== status) return false;
      if (filterLabel && c.labelId !== Number(filterLabel)) return false;
      if (filterAssignee && !(c.assignees || []).includes(Number(filterAssignee))) return false;
      return true;
    });
  };

  const handleAddCard = (defaultStatus) => setModal({ type: 'add', defaultStatus });
  const handleOpen = (card) => navigate(`/work/${department}/kanban/${card.id}`);

  const handleSave = async (form) => {
    const res = await api.post('/kanban/cards', { ...form, status: modal.defaultStatus || form.status, department: department || null });
    const newCard = res.data;
    newCard.assigneeDetails = (newCard.assignees || []).map(id => admins.find(a => a.id === id)).filter(Boolean);
    setCards(prev => [...prev, newCard]);
    setModal(null);
  };

  // Drag & Drop
  const findCard = (id) => cards.find(c => c.id === id);

  const handleDragStart = ({ active }) => {
    const card = findCard(active.id);
    setActiveCard(card);
    dragOriginStatus.current = card?.status ?? null;
    lastOverId.current = null;
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const dragged = findCard(active.id);
    if (!dragged) return;

    const overIsColumn = COLUMNS.some(c => c.id === over.id);
    const overCard = findCard(over.id);
    const newStatus = overIsColumn ? over.id : overCard?.status;

    if (!newStatus || newStatus === dragged.status) return;

    // 낙관적 업데이트: UI에서 즉시 컬럼 이동
    setCards(prev => prev.map(c => c.id === active.id ? { ...c, status: newStatus } : c));
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveCard(null);
    const originStatus = dragOriginStatus.current;
    dragOriginStatus.current = null;

    if (!over) {
      // 드롭 취소 → 원위치 복원
      if (originStatus) {
        setCards(prev => prev.map(c => c.id === active.id ? { ...c, status: originStatus } : c));
      }
      return;
    }

    const dragged = findCard(active.id); // handleDragOver 이후 현재 status
    if (!dragged) return;

    const overIsColumn = COLUMNS.some(c => c.id === over.id);
    const overCard = findCard(over.id);
    const newStatus = overIsColumn ? over.id : (overCard?.status || dragged.status);

    // 같은 컬럼 내 순서 변경
    const colCards = cards.filter(c => c.status === newStatus);
    const oldIndex = colCards.findIndex(c => c.id === active.id);
    const newIndex = overCard && !overIsColumn ? colCards.findIndex(c => c.id === over.id) : colCards.length - 1;

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const reordered = arrayMove(colCards, oldIndex, newIndex);
      const orders = reordered.map((c, i) => ({ id: c.id, sortOrder: i }));
      setCards(prev => {
        const others = prev.filter(c => c.status !== newStatus);
        return [...others, ...reordered.map((c, i) => ({ ...c, sortOrder: i }))];
      });
      await api.patch('/kanban/cards/reorder', { orders });
    }

    // originStatus와 비교해야 실제 컬럼 이동 여부를 정확히 판단
    if (originStatus && newStatus !== originStatus) {
      await api.patch(`/kanban/cards/${active.id}/move`, { status: newStatus, sortOrder: colCards.length });
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* 헤더 */}
      <PageHeader icon={KanbanSquare} title={department ? `${DEPT_LABEL[department]} 할일` : '칸반 보드'} count={cards.length}>
        {/* 라벨 필터 */}
        <select value={filterLabel} onChange={e => setFilterLabel(e.target.value)}
          className="bg-gray-900 border border-gray-700 text-gray-300 text-xs rounded-xl px-3 py-2 focus:outline-none">
          <option value="">전체 라벨</option>
          {labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {/* 담당자 필터 */}
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
          className="bg-gray-900 border border-gray-700 text-gray-300 text-xs rounded-xl px-3 py-2 focus:outline-none">
          <option value="">전체 담당자</option>
          {admins.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {/* 라벨 관리 */}
        <button onClick={() => setLabelModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800 hover:text-white">
          <Tag size={13} /> 라벨
        </button>
        {/* 카드 추가 */}
        <button onClick={() => handleAddCard('todo')}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80">
          <Plus size={13} /> 카드 추가
        </button>
      </PageHeader>

      {/* 칸반 보드 */}
      <div className="flex-1 overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex flex-col sm:flex-row gap-4 h-full pb-4 min-h-[400px]">
            {COLUMNS.map(col => (
              <Column
                key={col.id}
                col={col}
                cards={cardsInColumn(col.id)}
                onAddCard={handleAddCard}
                onOpen={handleOpen}
              />
            ))}
          </div>

          <DragOverlay>
            {activeCard && (
              <div className="rotate-1 scale-105">
                <KanbanCard card={activeCard} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* 카드 모달 */}
      {modal && (
        <CardModal
          card={modal.card}
          labels={labels}
          admins={admins}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {/* 라벨 관리 모달 */}
      {labelModal && (
        <LabelModal
          labels={labels}
          onClose={() => setLabelModal(false)}
          onSave={async ({ name, color }) => {
            const res = await api.post('/kanban/labels', { name, color });
            setLabels(prev => [...prev, res.data]);
          }}
          onDelete={async (id) => {
            await api.delete(`/kanban/labels/${id}`);
            setLabels(prev => prev.filter(l => l.id !== id));
          }}
        />
      )}
    </div>
  );
}
